import { useMemo, useCallback, useState, useEffect } from 'react';
import AuthService from '../../services/AuthService';
import type { UserPermissions } from '../../api/data-contracts';

/**
 * Centralized permission checking hook
 *
 * Replaces hardcoded role-based permission checks with backend-driven permissions.
 * All access control should use this hook instead of checking user.role directly.
 *
 * Returns computed boolean values that only change when actual permission values change,
 * not when the user object reference changes. This prevents unnecessary re-renders.
 *
 * @example
 * ```tsx
 * const { can, hasPermission, permissions, isLoading } = usePermissions();
 *
 * // Preferred: Use boolean properties (most efficient)
 * if (can.manageUsers) {
 *   // Show user management UI
 * }
 *
 * // Alternative: Use hasPermission function for dynamic checks
 * if (hasPermission('chat_config')) {
 *   // Show chat configuration
 * }
 * ```
 */
export const usePermissions = () => {
  // Use state to prevent infinite re-renders from new object references
  const [user, setUser] = useState(() => AuthService.getUser());

  // Listen for auth state changes
  useEffect(() => {
    const handleAuthChange = () => {
      setUser(AuthService.getUser());
    };

    window.addEventListener('authStateChanged', handleAuthChange);
    return () => window.removeEventListener('authStateChanged', handleAuthChange);
  }, []);

  // Loading state: true if user exists but permissions not loaded
  const isLoading = useMemo(() => {
    return user !== null && user?.permissions === undefined;
  }, [user]);

  // Serialize permissions for stable comparison - only changes when permission VALUES change
  const permissionsKey = useMemo(() => {
    if (!user?.permissions) return null;
    return JSON.stringify(user.permissions);
  }, [user?.permissions]);

  // Computed permission values - returns booleans instead of functions
  // Only recalculates when actual permission values change, not when user object reference changes
  const can = useMemo(() => {
    const checkPermission = (permission: keyof UserPermissions): boolean => {
      if (!user || !user.permissions) return false;
      return user.permissions[permission]?.is_visible ?? false;
    };

    return {
      // Basic features (standard users)
      viewDashboard: checkPermission('dashboard'),
      viewChat: checkPermission('chat'),
      viewDocuments: checkPermission('documents'),
      viewSettings: checkPermission('settings'),
      viewMyGroups: checkPermission('my_groups'),

      // User & group management (moderator+)
      manageUsers: checkPermission('users'),

      // Groups - Granular permissions
      manageGroups: user?.permissions?.groups?.create_group ?? false, // DEPRECATED: Use canCreateGroups instead
      canViewAllGroups: user?.permissions?.groups?.all_groups ?? false, // Can see all groups (moderator+)
      canCreateGroups: user?.permissions?.groups?.create_group ?? false, // Can create groups directly (moderator+)
      canRequestGroup: user?.permissions?.groups?.create_request ?? false, // Can request group creation (standard)

      // System configuration (admin+)
      configureChatModels: checkPermission('chat_config'),
      configureEmbedding: checkPermission('embedding_config'),
      configureTextExtraction: checkPermission('text_extraction_config'),
      configureDocumentSources: checkPermission('document_sources'),
      configureSMTP: checkPermission('smtp_config'),
    };
  }, [permissionsKey]); // Only depend on permissionsKey, not user object reference

  // Permission checker function - kept for backwards compatibility
  // Stabilized with permissionsKey to prevent unnecessary recreations
  const hasPermission = useCallback((permission: keyof UserPermissions): boolean => {
    if (!user || !user.permissions) {
      return false;
    }
    return user.permissions[permission]?.is_visible ?? false;
  }, [permissionsKey]); // Only depend on permissionsKey, not user object reference

  return {
    /**
     * Check if user has a specific permission (function for dynamic checks)
     * @param permission - Permission key from UserPermissions
     * @returns true if user has permission and it's visible
     */
    hasPermission,

    /**
     * Computed permission boolean values
     * These are stable and only change when actual permission values change,
     * preventing unnecessary re-renders
     */
    can,

    /**
     * Raw permissions object from backend
     */
    permissions: user?.permissions ?? null,

    /**
     * True if user is authenticated but permissions are still loading
     */
    isLoading,

    /**
     * Current user object (includes role and permissions)
     */
    user,
  };
};

/**
 * Type-safe permission key type
 */
export type PermissionKey = keyof UserPermissions;
